/**
 * LayersPanel - Manages base planes (layers)
 * Matches the Java SketchChair layers panel.
 *
 * Standard properties-list layout inside the right panel:
 * - Vertical scrollable list of layer rows (swatch with the layer number,
 *   "Layer N" label, "guide" tag). Click selects (shift = multi-select,
 *   both routed through selectBasePlaneWithMirror).
 * - Options area below the list (visible while at least one layer is
 *   selected): delete / mirror / select-all / guide-layer actions and the
 *   chair-width slider.
 *
 * The "+" add-layer button lives in the section header (UIManager) and
 * calls addLayer().
 */

import { ICONS, phIcon } from '../icons.js';
import { CurveEditor } from '../CurveEditor.js';

export class LayersPanel {
    constructor(application) {
        this.app = application;
        this.createPanel();
    }

    /**
     * Create the panel DOM structure
     */
    createPanel() {
        const panel = document.createElement('div');
        panel.className = 'layers-panel panel';
        panel.style.cssText = `
            display: flex;
            flex-direction: column;
            width: 100%;
        `;

        // Vertical scrollable layer list
        this.layerList = document.createElement('div');
        this.layerList.className = 'props-list layer-list';
        panel.appendChild(this.layerList);

        // Options area (visible only while at least one layer is selected)
        this.optionsArea = this.createOptionsArea();
        panel.appendChild(this.optionsArea);

        this.panel = panel;

        // Initial update
        this.update();
    }

    /**
     * Header "+" action (wired by UIManager): add a new layer
     */
    addLayer() {
        this.app.addNewLayer();
        this.update();
    }

    /**
     * Options area under the list: layer actions + chair width slider
     */
    createOptionsArea() {
        const area = document.createElement('div');
        area.className = 'props-options';

        // Action icon buttons
        const row = document.createElement('div');
        row.className = 'props-options-row';

        this.actionButtons = [];
        const controls = [
            { svg: ICONS.trash, tooltip: 'Delete Layer', action: () => {
                if (this.app.numBasePlanes > 1) {
                    this.app.numBasePlanes--;
                    this.app.initializeBasePlanes();
                    this.app.regenerateAll(); // canonical pipeline
                    this.update();
                }
            }},
            { svg: ICONS.flipHorizontal, tooltip: 'Mirror - select/unselect mirrored pairs together', action: () => {
                this.app.toggleMirrorMode();
                this.update();
            }, isActive: () => this.app.mirrorMode },
            { svg: ICONS.selectionAll, tooltip: 'Select All', action: () => {
                this.app.selectAllPlanes();
                this.update();
            }},
            // No obvious Phosphor equivalent - keep the legacy bitmap
            { img: 'GUI_LAYER_GUIDELAYER_BUTTON.png', tooltip: 'Toggle Guide Layer', action: () => {
                this.app.toggleGuideLayer();
                this.update();
            }}
        ];

        controls.forEach(control => {
            const button = this.createIconButton(control);
            row.appendChild(button);
            this.actionButtons.push({ button, control });
        });

        area.appendChild(row);

        // Chair width slider
        area.appendChild(this.createWidthControl());

        // Material thickness (design value; scrubable)
        area.appendChild(this.createThicknessControl());

        // Chair curve slider (parametric bulge across the width)
        area.appendChild(this.createCurveControl());

        return area;
    }

    /**
     * Create the DESIGN material-thickness control (label + scrubable
     * numeric, mm). This is the design value (app.setMaterialThickness);
     * the Make panel's "Thickness override" takes precedence for
     * fabrication when set.
     */
    createThicknessControl() {
        const container = document.createElement('div');
        container.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 6px;
            margin-top: 6px;
        `;

        const label = document.createElement('div');
        label.textContent = 'Thickness';
        label.className = 'props-field-label';
        label.title = 'Material thickness (mm). Drag the label to scrub.';
        label.style.cursor = 'ew-resize';
        container.appendChild(label);

        const input = document.createElement('input');
        input.type = 'number';
        input.min = '0.5';
        input.max = '50';
        input.step = '0.5';
        input.value = String(this.app.materialThickness ?? 5);
        input.style.cssText = 'width: 52px; font-size: 10px; padding: 1px 2px;';
        input.addEventListener('keydown', (e) => e.stopPropagation());
        container.appendChild(input);

        const unit = document.createElement('span');
        unit.textContent = 'mm';
        unit.style.cssText = 'color: var(--text-dim); font-size: 10px;';
        container.appendChild(unit);

        const apply = (v, throttled) => {
            if (!Number.isFinite(v)) return;
            v = Math.min(50, Math.max(0.5, v));
            if (!throttled) {
                this.app.setMaterialThickness(v);
                return;
            }
            const now = performance.now();
            if (now - (this._lastThicknessRegen || 0) >= 250) {
                this._lastThicknessRegen = now;
                this.app.setMaterialThickness(v);
            }
        };
        input.addEventListener('input', () => apply(parseFloat(input.value), true));
        input.addEventListener('change', () => apply(parseFloat(input.value), false));

        // Label scrub (0.25mm per px)
        label.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startV = parseFloat(input.value) || 5;
            try { label.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
            const onMove = (ev) => {
                const v = Math.min(50, Math.max(0.5,
                    Math.round((startV + (ev.clientX - startX) * 0.25) * 2) / 2));
                input.value = String(v);
                apply(v, true);
            };
            const onUp = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                apply(parseFloat(input.value), false);
            };
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
        });

        this.thicknessInput = input;
        return container;
    }

    /**
     * Create width control (label + slider + value readout)
     */
    createWidthControl() {
        const container = document.createElement('div');
        container.className = 'layer-width-control';
        container.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 4px;
        `;

        const labelRow = document.createElement('div');
        labelRow.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
        `;

        const label = document.createElement('div');
        label.textContent = 'Chair width';
        label.className = 'props-field-label';
        labelRow.appendChild(label);

        const widthDisplay = document.createElement('div');
        widthDisplay.textContent = this.app.chairWidth.toFixed(2);
        widthDisplay.style.cssText = `
            color: var(--text-dim);
            font-size: 11px;
        `;
        labelRow.appendChild(widthDisplay);
        container.appendChild(labelRow);

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = '100';
        slider.max = '600';
        slider.value = this.app.chairWidth.toString();
        slider.style.cssText = `
            width: 100%;
        `;
        slider.addEventListener('input', (e) => {
            const width = parseFloat(e.target.value);
            this.widthDisplay.textContent = width.toFixed(2);
            this.app.setChairWidth(width);
        });
        container.appendChild(slider);

        this.widthSlider = slider;
        this.widthDisplay = widthDisplay;

        return container;
    }

    /**
     * Create the CHAIR CURVE control (label + slider + value readout):
     * bows the chair across its width. At each layer's cross-chair t the
     * store's chair curve (default parabolic - see
     * StrokeStore.curveEquations for the pluggable equation registry)
     * radially swells (positive, barrel) or pinches (negative, waist)
     * every LINKED stroke about its own centroid. Detached copies are
     * untouched, curve changes never detach, and the value is part of the
     * document (schema v2) and of undo snapshots.
     */
    createCurveControl() {
        const container = document.createElement('div');
        container.className = 'layer-curve-control';
        container.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 4px;
        `;

        const labelRow = document.createElement('div');
        labelRow.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
        `;

        const label = document.createElement('div');
        label.textContent = 'Curve';
        label.className = 'props-field-label';
        labelRow.appendChild(label);

        const curveDisplay = document.createElement('div');
        curveDisplay.style.cssText = `
            color: var(--text-dim);
            font-size: 11px;
        `;
        labelRow.appendChild(curveDisplay);
        container.appendChild(labelRow);

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = '-200';
        slider.max = '200';
        slider.step = '1';
        slider.value = String(this.app.strokeStore?.getChairCurve?.().amount ?? 0);
        slider.title = 'Bow the chair across its width: + barrel, - waist';
        slider.style.cssText = `
            width: 100%;
        `;
        curveDisplay.textContent = `${slider.value} mm`;

        // ONE undo snapshot per slider gesture
        slider.addEventListener('pointerdown', () => {
            this._curveGestureSnapshotted = false;
        });
        const setAmount = (amount, throttled) => {
            const store = this.app.strokeStore;
            if (!store) return;
            if (!this._curveGestureSnapshotted) {
                this._curveGestureSnapshotted = true;
                this.app.pushUndoSnapshot();
            }
            store.setChairCurve({ ...store.getChairCurve(), amount });
            curveDisplay.textContent = `${amount} mm`;
            if (!throttled) {
                this.app.commitStoreMutation();
                return;
            }
            const now = performance.now();
            if (now - (this._lastCurveRegen || 0) >= 150) {
                this._lastCurveRegen = now;
                this.app.commitStoreMutation();
            }
        };
        slider.addEventListener('input', (e) => {
            setAmount(parseFloat(e.target.value) || 0, true);
        });
        // Final, unthrottled commit at gesture end
        slider.addEventListener('change', (e) => {
            setAmount(parseFloat(e.target.value) || 0, false);
            this._curveGestureSnapshotted = false;
        });
        container.appendChild(slider);

        this.curveSlider = slider;
        this.curveDisplay = curveDisplay;

        // --- End align slider: blends the length profile's endpoint
        // weights toward 0 so stroke ENDS tween into exact alignment
        // across layers (1 = ends coincident)
        const eaRow = document.createElement('div');
        eaRow.style.cssText = `
            display: flex; align-items: center; justify-content: space-between;
            margin-top: 6px;`;
        const eaLabel = document.createElement('div');
        eaLabel.textContent = 'End align';
        eaLabel.className = 'props-field-label';
        eaRow.appendChild(eaLabel);
        const eaDisplay = document.createElement('div');
        eaDisplay.style.cssText = 'color: var(--text-dim); font-size: 11px;';
        eaRow.appendChild(eaDisplay);
        container.appendChild(eaRow);

        const eaSlider = document.createElement('input');
        eaSlider.type = 'range';
        eaSlider.min = '0';
        eaSlider.max = '1';
        eaSlider.step = '0.01';
        eaSlider.value = String(this.app.strokeStore?.getChairCurve?.().endAlign ?? 0);
        eaSlider.title = 'Fade the bulge at stroke ends: 1 = ends align exactly across layers';
        eaSlider.style.cssText = 'width: 100%;';
        eaDisplay.textContent = `${Math.round(parseFloat(eaSlider.value) * 100)}%`;

        eaSlider.addEventListener('pointerdown', () => {
            this._eaGestureSnapshotted = false;
        });
        const setEndAlign = (endAlign, throttled) => {
            const store = this.app.strokeStore;
            if (!store) return;
            if (!this._eaGestureSnapshotted) {
                this._eaGestureSnapshotted = true;
                this.app.pushUndoSnapshot();
            }
            store.setChairCurve({ ...store.getChairCurve(), endAlign });
            eaDisplay.textContent = `${Math.round(endAlign * 100)}%`;
            if (!throttled) {
                this.app.commitStoreMutation();
                return;
            }
            const now = performance.now();
            if (now - (this._lastCurveRegen || 0) >= 150) {
                this._lastCurveRegen = now;
                this.app.commitStoreMutation();
            }
        };
        eaSlider.addEventListener('input', (e) => setEndAlign(parseFloat(e.target.value) || 0, true));
        eaSlider.addEventListener('change', (e) => {
            setEndAlign(parseFloat(e.target.value) || 0, false);
            this._eaGestureSnapshotted = false;
        });
        container.appendChild(eaSlider);

        this.endAlignSlider = eaSlider;
        this.endAlignDisplay = eaDisplay;

        // --- Length profile editor: how the bulge applies ALONG each
        // stroke (arc position s -> weight w; shared CurveEditor widget)
        const lpRow = document.createElement('div');
        lpRow.style.cssText = `
            display: flex; align-items: center; justify-content: space-between;
            margin-top: 6px;`;
        const lpLabel = document.createElement('div');
        lpLabel.textContent = 'Length profile';
        lpLabel.className = 'props-field-label';
        lpRow.appendChild(lpLabel);

        const mkPtBtn = (text, title, fn) => {
            const b = document.createElement('button');
            b.textContent = text;
            b.title = title;
            b.style.cssText = `
                width: 24px; padding: 2px 0; font-size: 12px; border-radius: 6px;
                border: 1px solid rgba(0,0,0,0.15); background: transparent; cursor: pointer;`;
            b.addEventListener('click', fn);
            return b;
        };
        const btnWrap = document.createElement('div');
        btnWrap.style.cssText = 'display: flex; gap: 4px;';
        lpRow.appendChild(btnWrap);
        container.appendChild(lpRow);

        this.lengthProfileEditor = new CurveEditor({
            min: 0,
            max: 1.5,
            identity: 1,
            sKey: 's',
            vKey: 'w',
            getPoints: () => this.app.strokeStore?.getChairCurve?.().lengthProfile || null,
            setPoints: (pts) => {
                const store = this.app.strokeStore;
                if (!store) return;
                store.setChairCurve({ ...store.getChairCurve(), lengthProfile: pts });
            },
            onGestureStart: () => this.app.pushUndoSnapshot(),
            onChangeThrottled: () => {
                const now = performance.now();
                if (now - (this._lastCurveRegen || 0) >= 150) {
                    this._lastCurveRegen = now;
                    this.app.commitStoreMutation();
                }
            },
            onCommit: () => this.app.commitStoreMutation()
        });
        btnWrap.appendChild(mkPtBtn('+', 'Add a profile point (max 5)',
            () => this.lengthProfileEditor.addPoint()));
        btnWrap.appendChild(mkPtBtn('−', 'Remove a profile point (min 2)',
            () => this.lengthProfileEditor.removePoint()));
        container.appendChild(this.lengthProfileEditor.element);

        const lpCaption = document.createElement('div');
        lpCaption.className = 'props-hint';
        lpCaption.style.padding = '0 2px';
        lpCaption.textContent = 'stroke start → end';
        container.appendChild(lpCaption);
        this.lengthProfileEditor.draw();

        return container;
    }

    /**
     * Create a small icon button (Phosphor SVG or legacy bitmap)
     */
    createIconButton(control) {
        const button = document.createElement('button');
        button.title = control.tooltip;
        button.className = 'props-icon-btn';

        if (control.svg) {
            button.appendChild(phIcon(control.svg, 20));
        } else {
            const icon = document.createElement('img');
            icon.src = `/images/gui/${control.img}`;
            icon.alt = control.tooltip;
            button.appendChild(icon);
        }

        button.addEventListener('click', control.action);
        return button;
    }

    /**
     * Create one layer row: swatch (layer number) + label + optional
     * "guide" tag. Click = select (shift for multi-select), preserving the
     * mirror-aware selection behavior.
     */
    createLayerRow(index, isSelected) {
        const basePlane = this.app.basePlanes[index];
        const isGuide = basePlane ? basePlane.guide : false;

        const row = document.createElement('div');
        row.className = 'props-row layer-item';
        row.dataset.index = index;
        if (isSelected) row.classList.add('selected');

        // Swatch chip with the layer number
        const swatch = document.createElement('div');
        swatch.className = 'props-swatch';
        if (isGuide) {
            swatch.style.background = 'rgba(255, 170, 0, 0.6)';
            swatch.style.border = '1px solid #FFAA00';
            swatch.style.color = '#fff';
        } else if (isSelected) {
            swatch.style.background = 'var(--accent-tint-strong)';
            swatch.style.border = '1px solid var(--accent)';
            swatch.style.color = 'var(--accent-strong)';
        } else {
            swatch.style.background = 'rgba(0, 0, 0, 0.06)';
            swatch.style.border = '1px solid rgba(0, 0, 0, 0.14)';
            swatch.style.color = '#999';
        }
        swatch.textContent = (index + 1).toString();
        row.appendChild(swatch);

        // Label
        const label = document.createElement('div');
        label.className = 'props-row-label';
        label.textContent = `Layer ${index + 1}`;
        row.appendChild(label);

        // Guide tag
        if (isGuide) {
            const tag = document.createElement('span');
            tag.className = 'props-tag';
            tag.textContent = 'guide';
            row.appendChild(tag);
        }

        // Click handler (shift = multi-select, both mirror-aware)
        row.addEventListener('click', (e) => {
            this.app.selectBasePlaneWithMirror(index, e.shiftKey);
            this.update();
        });

        return row;
    }

    /**
     * Get the panel element
     */
    getElement() {
        return this.panel;
    }

    /**
     * Update panel to reflect current state
     */
    update() {
        // Rebuild the layer list
        this.layerList.innerHTML = '';

        const selectedIndices = this.app.selectedBasePlaneIndices;

        for (let i = 0; i < this.app.basePlanes.length; i++) {
            const isSelected = selectedIndices.has(i);
            const row = this.createLayerRow(i, isSelected);
            this.layerList.appendChild(row);
        }

        // Options only while something is selected
        this.optionsArea.style.display = selectedIndices.size > 0 ? 'flex' : 'none';

        // Refresh toggle-button active states (mirror mode)
        for (const { button, control } of this.actionButtons) {
            button.classList.toggle('active', !!(control.isActive && control.isActive()));
        }

        // Keep the width slider in sync with the app state
        if (document.activeElement !== this.widthSlider) {
            this.widthSlider.value = this.app.chairWidth.toString();
            this.widthDisplay.textContent = this.app.chairWidth.toFixed(2);
        }

        // Keep the thickness input in sync with the design value
        if (this.thicknessInput && document.activeElement !== this.thicknessInput) {
            this.thicknessInput.value = String(this.app.materialThickness ?? 5);
        }

        // Keep the chair-curve controls in sync with the store (undo,
        // save/load and clears all change it out from under the UI)
        const curve = this.app.strokeStore?.getChairCurve?.();
        if (curve) {
            if (this.curveSlider && document.activeElement !== this.curveSlider) {
                this.curveSlider.value = String(curve.amount ?? 0);
                this.curveDisplay.textContent = `${curve.amount ?? 0} mm`;
            }
            if (this.endAlignSlider && document.activeElement !== this.endAlignSlider) {
                this.endAlignSlider.value = String(curve.endAlign ?? 0);
                this.endAlignDisplay.textContent = `${Math.round((curve.endAlign ?? 0) * 100)}%`;
            }
            if (this.lengthProfileEditor && !this.lengthProfileEditor.isDragging) {
                this.lengthProfileEditor.draw();
            }
        }
    }
}
